import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Grid, MenuItem, Button, CircularProgress } from "@material-ui/core";
import { ToggleButtonGroup, ToggleButton } from "@material-ui/lab";
import UploadIcon from "@material-ui/icons/CloudUpload";
import DeleteIcon from "@material-ui/icons/Delete";

import FloatingPanel from "./FloatingPanel";
import LayerWithType from "./LayerWithType";
import HexFillOptions from "./HexFillOptions";
import { FilledSelect, FilledTextField } from "../../components/FormElements";
import { FleetContext, FleetSettingsContext, GeofencesContext } from "../../context";
import {
  GeofencesLayerTypes,
  GeofencesSpeedLayers,
  GeofencesIncentiveLayers,
  GeofencesLayersToFill,
  GeofencesSpeedDimensions,
  GeofencesPanelPositions,
  GeofencesEditModes,
  GeofencesSpeedRange,
  GeofencesIncentiveRange,
  SidewalksLayers,
} from "../../constants";
import { useDialog } from "../../hooks/useDialog";
import buildAssetsUrl from "../../util/strings/buildAssetsUrl";

const useStyles = makeStyles((theme) => ({
  rightSideElement: { marginLeft: "auto" },
  buttonIcon: { marginRight: theme.spacing(1) },
  toggleButtonGroup: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(3),
  },
  inputGroup: {
    marginLeft: theme.spacing(2),
    "& > div": { minWidth: 80 },
  },
  inputText: {
    width: 100,
    margin: 0,
    marginRight: theme.spacing(1),
    "& > div": { borderRadius: 0 },
  },
  inputTextLarge: { width: 125 },
}));

const KilometersToMiles = (km) => Math.round((km / 1.609) * 10) / 10;
const MilesToKilometers = (mi) => Math.round(mi * 1.609 * 10) / 10;

const EditLayerPanel = ({ sidewalksMode, onChangeUploadLoading }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();

  const [openDeleteDialog, deleteDialog] = useDialog({
    title: t("Fleet.Geofences.Dialogs.DeleteLayerTitle"),
    body: t("Fleet.Geofences.Dialogs.DeleteLayerBody"),
  });

  const [openUploadLargeLayerDialog, uploadLargeLayerDialog] = useDialog({
    title: t("Fleet.Geofences.Dialogs.UploadLargeLayerTitle"),
    body: t("Fleet.Geofences.Dialogs.UploadLargeLayerBody"),
  });

  const { activeRatePlan, fetchRatePlan } = useContext(FleetSettingsContext);
  const { activeFleet } = useContext(FleetContext);

  const { layers, selectedLayer, updateSelectedLayer, updateLayer, deleteLayer, uploadLayer } =
    useContext(GeofencesContext);

  const layer = layers[selectedLayer.key] || {};
  const persistedLayer = Object.values(layer.zones || {}).some((zone) => zone.id);

  const canUploadLayer =
    layer.type !== GeofencesLayerTypes.none &&
    Object.values(layer.zones || {}).some((zone) => !zone.id && Object.keys(zone.hexes).length);

  const { editMode } = selectedLayer;
  const { speed, incentive, type } = layer;

  const [showSpeedInput, setShowSpeedInput] = useState(GeofencesSpeedLayers.includes(type));
  const [speedDimension, setSpeedDimension] = useState(GeofencesSpeedDimensions.km.key);

  const [showIncentiveInput, setShowIncentiveInput] = useState(
    GeofencesIncentiveLayers.includes(type),
  );

  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    if (!activeRatePlan && activeFleet) {
      fetchRatePlan(activeFleet.rate_plan_id);
    }
  }, [activeFleet?.rate_plan_id, activeRatePlan]);

  useEffect(() => {
    setShowSpeedInput(GeofencesSpeedLayers.includes(type));
    setShowIncentiveInput(GeofencesIncentiveLayers.includes(type));
  }, [type]);

  const speedValue =
    speedDimension === GeofencesSpeedDimensions.km.key ? speed : KilometersToMiles(speed);

  const availableLayerTypes = Object.values(GeofencesLayerTypes).filter(
    (layerType) => !sidewalksMode || SidewalksLayers.includes(layerType),
  );

  const onEditModeChange = (_, v) => updateSelectedLayer({ editMode: v });
  const onDeleteClick = () => openDeleteDialog(() => () => deleteLayer(selectedLayer.key));

  const onSetLayerType = (e) => updateLayer(selectedLayer.key, { type: e.target.value });

  const onSetSpeedDimension = (e) => setSpeedDimension(e.target.value);

  const onSetSpeedValue = (e) => {
    const targetValue = e.target.value;
    const isInKm = speedDimension === GeofencesSpeedDimensions.km.key;
    const value = !targetValue || isInKm ? targetValue : MilesToKilometers(targetValue);

    updateLayer(selectedLayer.key, {
      speed: value
        ? Math.max(Math.min(value, GeofencesSpeedRange.max), GeofencesSpeedRange.min)
        : value,
    });
  };

  const onSetIncentiveValue = (e) => {
    updateLayer(selectedLayer.key, {
      incentive: {
        amount: e.target.value,
        currency: layers[selectedLayer.key].incentive?.currency || activeRatePlan.currency,
      },
    });
  };

  const onUploadLayer = async () => {
    onChangeUploadLoading(true);
    setUploadLoading(true);
    await uploadLayer({
      key: layer.key,
      marketUrl: activeFleet.market,
      fleetUrl: activeFleet.url,
    });
    setUploadLoading(false);
    onChangeUploadLoading(false);
  };

  const onUploadClick = () => {
    const zoneCount = Object.keys(layers[selectedLayer.key].zones).length;
    if (zoneCount > (window.REACT_APP_GEOFENCE_BULK_UPLOAD_WARNING_THRESHOLD || 100))
      openUploadLargeLayerDialog(() => () => onUploadLayer());
    else
      onUploadLayer();
  };

  const isUploadableLayerType =
    sidewalksMode || layers[selectedLayer.key].type === GeofencesLayerTypes.parkingAllowedZone;

  return (
    <FloatingPanel position={GeofencesPanelPositions.TOP}>
      <Grid item container alignItems="center">
        <FilledSelect
          label={t("Fleet.Geofences.LayerSelectLabel")}
          value={type || GeofencesLayerTypes.none}
          onChange={onSetLayerType}
        >
          {availableLayerTypes.map((layerType) => (
            <MenuItem key={layerType} value={layerType}>
              <LayerWithType type={layerType}>
                {t(`Fleet.Geofences.LayerTypes.${layerType}`)}
              </LayerWithType>
            </MenuItem>
          ))}
        </FilledSelect>
        {showSpeedInput && (
          <Grid item className={classes.inputGroup}>
            <FilledTextField
              value={speedValue === null ? "" : speedValue}
              type="number"
              label={t("Fleet.Geofences.LayerSpeedLabel")}
              className={classes.inputText}
              onChange={onSetSpeedValue}
              underlineColor="white"
            />
            <FilledSelect value={speedDimension} onChange={onSetSpeedDimension}>
              {Object.values(GeofencesSpeedDimensions).map((dimension) => (
                <MenuItem key={dimension.key} value={dimension.key}>
                  {dimension.value}
                </MenuItem>
              ))}
            </FilledSelect>
          </Grid>
        )}
        {showIncentiveInput && (
          <Grid item className={classes.inputGroup}>
            <FilledTextField
              value={incentive.amount || ""}
              label={`${t("Fleet.Geofences.LayerIncentiveLabel")}
-                (${incentive.currency || activeRatePlan.currency})`}
              mask={incentive.currency || activeRatePlan.currency}
              className={clsx(classes.inputText, classes.inputTextLarge)}
              onChange={onSetIncentiveValue}
              underlineColor="white"
              error={
                !incentive.amount || parseInt(incentive.amount, 10) < GeofencesIncentiveRange.min
              }
              helperText={
                (!incentive.amount ||
                  parseInt(incentive.amount, 10) < GeofencesIncentiveRange.min) &&
                t("Fleet.Geofences.LayerIncentiveWarning")
              }
            />
          </Grid>
        )}
        {!sidewalksMode && (
          <ToggleButtonGroup
            value={editMode}
            size="small"
            exclusive
            onChange={onEditModeChange}
            className={classes.toggleButtonGroup}
          >
            <ToggleButton value={GeofencesEditModes.polygonCreate}>
              <img src={buildAssetsUrl("geofences/polygon-plus-icon.svg")} alt="polygon-plus" />
            </ToggleButton>
            <ToggleButton value={GeofencesEditModes.hex}>
              <img src={buildAssetsUrl("geofences/hex-plus-icon.svg")} alt="hex" />
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        {!sidewalksMode && GeofencesLayersToFill.includes(type) && (
          <HexFillOptions sidewalksMode={sidewalksMode} />
        )}
        <div className={classes.rightSideElement}>
          {isUploadableLayerType && Object.keys(layer.zones).length > 0 && (
            <Button
              onClick={onUploadClick}
              color="secondary"
              disabled={uploadLoading || !canUploadLayer}
            >
              {uploadLoading ? (
                <CircularProgress size={24} color="secondary" />
              ) : (
                <Grid item container>
                  <UploadIcon className={classes.buttonIcon} />
                  {t("Fleet.Geofences.Buttons.Upload")}
                </Grid>
              )}
            </Button>
          )}
          {!persistedLayer && (
            <Button onClick={onDeleteClick} disabled={!!editMode}>
              <DeleteIcon className={classes.buttonIcon} />
              {t("Fleet.Geofences.Buttons.Delete")}
            </Button>
          )}
        </div>
      </Grid>
      {uploadLargeLayerDialog}
      {deleteDialog}
    </FloatingPanel>
  );
};

EditLayerPanel.propTypes = {
  sidewalksMode: PropTypes.bool,
  onChangeUploadLoading: PropTypes.func,
};

EditLayerPanel.defaultProps = {
  sidewalksMode: false,
  onChangeUploadLoading: () => null,
};

export default EditLayerPanel;
